import React, { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import axios from "axios";
import moment from "moment";
import { FaPhoneAlt, FaWhatsapp, FaMale, FaFemale, FaCheck } from "react-icons/fa";
import { BiMaleFemale } from "react-icons/bi";
import { BsFileText } from "react-icons/bs";
import { AiOutlineLock } from "react-icons/ai";
import Config from "../../Config";
import { capitalizeFirstLetter } from "../../helpers";
import { ModalHeader } from "../Styles/InputStyles";
import NotFoundModel from "../NotFoundModel";

/* ---------- OCCUPANCY STATUS META (isEvicted = 0) ---------- */
const OCCUPANCY_STATUS_META = {
    [Config.OCCUPANCY_STATUS.PENDING]: {
        label: "Pending",
        className: "bg-amber-50 text-amber-700",
    },
    [Config.OCCUPANCY_STATUS.REQUESTED]: {
        label: "Requested",
        className: "bg-indigo-50 text-indigo-600",
    },
    [Config.OCCUPANCY_STATUS.OCCUPIED]: {
        label: "Occupied",
        className: "bg-green-50 text-green-600",
    },
    [Config.OCCUPANCY_STATUS.MOVING_OUT]: {
        label: "Moving Out",
        className: "bg-orange-50 text-orange-600",
    },
    [Config.OCCUPANCY_STATUS.RESERVED]: {
        label: "Reserved",
        className: "bg-blue-50 text-blue-600",
    },
};

/* ---------- MOVE-OUT STATUS META (isEvicted = 1) ---------- */
const MOVE_OUT_STATUS_META = {
    [Config.MOVE_OUT_STATUS.INITIATED]: {
        label: "Evicted",
        className: "bg-red-50 text-red-600",
    },
    [Config.MOVE_OUT_STATUS.MOVEOUT]: {
        label: "Moved Out",
        className: "bg-gray-100 text-gray-600",
    },
};

/* ---------- GENDER META (uses Config.GENDER_PREFERENCE) ---------- */
const GENDER_META = {
    [Config.GENDER_PREFERENCE.MALE]: {
        label: "Male",
        Icon: FaMale,
        className: "bg-blue-50 text-blue-600",
    },
    [Config.GENDER_PREFERENCE.FEMALE]: {
        label: "Female",
        Icon: FaFemale,
        className: "bg-pink-50 text-pink-600",
    },
    [Config.GENDER_PREFERENCE.BOTH]: {
        label: "Co-Living",
        Icon: BiMaleFemale,
        className: "bg-purple-50 text-purple-600",
    },
};

const BedHistory = ({ handleClose, selectedData }) => {
    const user = useSelector((state) => state.UserReducer.user);
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchBedHistory = async () => {
        const response = await axios.get(
            `${Config.apiUrl}/room/bed/history/${selectedData?.id}`,
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const {
        data,
        isLoading: historyLoading,
        isError: historyError,
        error,
        isRefetching
    } = useQuery(["bedHistory", selectedData?.id], fetchBedHistory, {
        enabled: !!selectedData?.id && !!user?.token,
        refetchOnWindowFocus: false,
    });

    const historyData = data?.data === false ? [] : data?.data || [];

    /* -------- status derived from isEvicted -------- */
    const getStatusMeta = (item) => {
        const isEvicted = Number(item?.isEvicted) === 1;

        if (isEvicted) {
            return (
                MOVE_OUT_STATUS_META[item?.status] || {
                    label: "Evicted",
                    className: "bg-red-50 text-red-600",
                }
            );
        }

        return (
            OCCUPANCY_STATUS_META[item?.status] || {
                label: "Unknown",
                className: "bg-gray-100 text-gray-600",
            }
        );
    };

    const getGenderMeta = (gender) =>
        GENDER_META[gender] || GENDER_META[Config.GENDER_PREFERENCE.BOTH];

    /* -------- agreement period (in months) from moveIn → moveOut -------- */
    const getAgreementMonths = (item) => {
        if (item?.agreementPeriod) return `${item.agreementPeriod} Month`;
        return null;
    };

    /* -------- lock-in period -------- */
    const getLockIn = (item) => {
        if (item?.lockInPeriod) {
            return `${item.lockInPeriod}M Lock-in`;
        }
        return null;
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            {/* ---------- HEADER ---------- */}
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Bed History</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            {/* ---------- LOADING ---------- */}
            {(historyLoading || isRefetching) &&  (
                <div className="px-4 py-4 space-y-3">
                    {[1, 2].map((i) => (
                        <div
                            key={i}
                            className="h-56 bg-white rounded-lg animate-pulse"
                        />
                    ))}
                </div>
            )}

            {/* ---------- ERROR ---------- */}
            {historyError && (
                <div className="px-4 py-4 text-sm text-red-500">
                    {error?.response?.data?.msg || "Failed to load bed history"}
                </div>
            )}

            {/* ---------- DATA ---------- */}
            {!historyLoading && !historyError && historyData && !isRefetching && (
                <Content>
                    {historyData?.length > 0 ? (
                        historyData.map((item, index) => {
                            const statusMeta = getStatusMeta(item);
                            const genderMeta = getGenderMeta(item?.gender);
                            const GenderIcon = genderMeta.Icon;
                            const agreementMonths = getAgreementMonths(item);
                            const lockInPeriod = getLockIn(item);

                            return (
                                <HistoryCard key={item.id || index}>
                                    {/* -------- TOP: avatar, name, status -------- */}
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <Avatar>
                                                {item?.profilePicture ? (
                                                    <img
                                                        src={item.profilePicture}
                                                        alt={item.tenantName}
                                                        className="w-full h-full object-cover rounded-full"
                                                    />
                                                ) : (
                                                    <span className="text-gray-500 font-medium text-base">
                                                        {item.tenantName?.charAt(0)?.toUpperCase()}
                                                    </span>
                                                )}
                                            </Avatar>

                                            <div className="flex flex-col min-w-0">
                                                <span className="text-gray-900 font-semibold text-base truncate">
                                                    {capitalizeFirstLetter(item?.tenantName || "N/A")}
                                                </span>
                                                <span className="text-sm text-gray-500 truncate">
                                                    Room{" "}
                                                    {selectedData?.roomNum || item?.roomNum || "-"}
                                                </span>
                                            </div>
                                        </div>

                                        <StatusPill className={statusMeta.className}>
                                            {statusMeta.label}
                                        </StatusPill>
                                    </div>

                                    {/* -------- GENDER + CALL/WHATSAPP -------- */}
                                    <div className="flex items-center justify-between mt-4">
                                        <span
                                            className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full ${genderMeta.className}`}
                                        >
                                            <GenderIcon size={12} />
                                            {genderMeta.label}
                                        </span>

                                        <div className="flex items-center gap-3">
                                            {item?.mobile && (
                                                <>
                                                    <IconBtn
                                                        as="a"
                                                        href={`tel:${item.mobile}`}
                                                        title="Call"
                                                    >
                                                        <FaPhoneAlt
                                                            size={14}
                                                            className="text-gray-600"
                                                        />
                                                    </IconBtn>

                                                    <IconBtn
                                                        as="a"
                                                        href={`https://wa.me/${item.mobile}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="WhatsApp"
                                                    >
                                                        <FaWhatsapp
                                                            size={16}
                                                            className="text-green-600"
                                                        />
                                                    </IconBtn>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* -------- DIVIDER -------- */}
                                    <Divider />

                                    {/* -------- RENT + DATES -------- */}
                                    <div className="flex items-start justify-between">
                                        <div className="flex flex-col">
                                            <span className="text-gray-900 font-semibold text-lg">
                                                ₹{(item?.rent || 0).toLocaleString()}
                                            </span>
                                            <span className="text-xs text-gray-500 mt-0.5">
                                                Rent
                                            </span>
                                        </div>

                                        <div className="flex flex-col items-end gap-2">
                                            {item?.moveInDate && (
                                                <div className="flex flex-col items-end">
                                                    <span className="text-gray-800 font-medium text-sm">
                                                        {moment(item.moveInDate).format("DD, MMM YYYY")}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        Move In
                                                    </span>
                                                </div>
                                            )}

                                            {item?.moveOutDate && (
                                                <div className="flex flex-col items-end">
                                                    <span className="text-gray-800 font-medium text-sm">
                                                        {moment(item.moveOutDate).format("DD, MMM YYYY")}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        Move Out
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* -------- AGREEMENT + LOCK-IN -------- */}
                                    {(agreementMonths || lockInPeriod) && (
                                        <div className="flex flex-wrap gap-2 mt-4">
                                            {agreementMonths && (
                                                <Chip className="bg-blue-50 text-blue-600">
                                                    <BsFileText size={13} />
                                                    {agreementMonths}
                                                </Chip>
                                            )}

                                            {lockInPeriod && (
                                                <Chip className="bg-purple-50 text-purple-600">
                                                    <AiOutlineLock size={13} />
                                                    {lockInPeriod}
                                                </Chip>
                                            )}
                                        </div>
                                    )}
                                </HistoryCard>
                            );
                        })
                    ) : (
                        <EmptyState><NotFoundModel /></EmptyState>
                        // <NotFoundModel />
                    )}
                </Content>
            )}
        </Container>
    );
};

/* ---------------------- STYLES ---------------------- */

const Container = tw.div`bg-[#f7f8fa] w-full h-full flex flex-col relative`;

const Content = tw.div`px-4 py-4 space-y-4 overflow-y-auto`;

const HistoryCard = tw.div`
  bg-white rounded-lg p-4 shadow-sm border border-gray-100
`;

const Avatar = tw.div`
  w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0
`;

const StatusPill = tw.span`
  text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap
`;

const IconBtn = tw.button`
  w-10 h-10 rounded-full bg-gray-100
  flex items-center justify-center
  hover:bg-gray-200 transition-colors
`;

const Divider = tw.div`h-px bg-gray-100 my-4`;

const Chip = tw.span`
  inline-flex items-center gap-1.5
  text-xs font-medium px-3 py-1.5 rounded-full
`;

const EmptyState = tw.div`
  text-center text-gray-500 text-sm py-20
`;

export default BedHistory;